import React from 'react';
import Layout from 'src/components/common/theme/Layout';
import { css, useTheme } from 'styled-components';
import { SlickSlider } from 'src/components/common/modules/carousel/slick/slick-carousel';
import { ImageCard } from 'src/components/common/theme/component';
import { checkValue, LightenDarkenColor, dealHelper, appendMcicid } from 'src/components/common/helper';
import { Typo } from 'src/components/common/ui';
import { UitkButtonSize } from 'uitk-react-button';
import { PrimaryButton, PrimaryButtonProps } from 'src/components/common/ui/button/button';
import {
  ActivityDataFetch,
  ActivityDataFetchProps,
} from 'src/components/common/modules/expedia-deals/activity-deals/activity-data/activity-data-fetch';
import { skeleton } from './skeleton';
import * as config from 'src/config/config';


// Css Style Import
import(/* webpackChunkName: "activity-view-styles" */ 'src/components/common/modules/expedia-deals/activity-deals/styles/activity-view.css');

const gaTracking = {
  eventCategory: 'Slick carousel',
  eventAction: 'Content Tile - Carousel',
  eventLabel: 'slide-',
};

const ActivityModule = (props: any) => {
  const {
    data: {
      headline,
      subheadline,
      backgroundColor,
      textColor,
      regionId,
      regionName,
      startDate,
      endDate,
      activityCtaText,
      activityCTAColor,
      activityCTATextColor,
      propertyCount,
      galleryTileSize = 'Large',
      backgroundBarColor,
      activityItemList
    },
    id,
    mccidData: {
      campaignUrl,
      referrerId,
      templateType
    }
  } = props;

  const theme = useTheme();
  const fontWeight = theme?.styles?.fontWeight;

  let isCMSData = false;
  isCMSData = checkValue(activityItemList) ? true : false;

  const settings = {
    arrows: true,
    slidesToShow: 3,
    infinite: false,
    dots: true,
    responsive: [
      {
        breakpoint: 575,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          centerMode: true,
          centerPadding: '20px',
          arrows: true,
          infinite: true,
          dots: true,
        },
      },
    ],
  };

  const { checkInDateFormattedInputFormat, checkOutDateFormattedInputFormat } = dealHelper.getCheckInCheckOutDates(
    startDate,
    endDate
  );

  const activityDataFetchProps: ActivityDataFetchProps = {
    regionId: regionId,
    regionName: regionName,
    startDate: checkInDateFormattedInputFormat,
    endDate: checkOutDateFormattedInputFormat,
    size: checkValue(propertyCount) ? Number(propertyCount) : 50
  };

  const { data, loading } = ActivityDataFetch(activityDataFetchProps);

  
  const activityDeals = 
    checkValue(data?.activitySearch?.activityGroups) &&
    data?.activitySearch?.activityGroups.find(
      (el: any) => el.type === 'TOP_ACTIVITIES' && el?.viewType === 'ACTIVITY_TILES_CAROUSEL'
    );

  const primaryButtonProps: PrimaryButtonProps = {
    text: '',
    tag: 'button',
    gaTracking: {
      eventCategory: 'Button Click',
      eventAction: 'Activity Deals Click',
      eventLabel: activityCtaText,
    },
    exClass: `btn mobile-btn`,
    size: UitkButtonSize.LARGE,
  };

  const tileBody = (tileTitle: string, tileDescription: string) => {
    return (
      <>
        {checkValue(tileTitle) && (
          <Typo.HTMLText tag="h2" exClass="txtBold activity-deals-body-text title">
            {tileTitle}
          </Typo.HTMLText>
        )}
        {checkValue(tileDescription) && (
          <Typo.HTMLText tag="h3" exClass="activity-deals-body-text sub-title">
            {tileDescription}
          </Typo.HTMLText>
        )}
      </>
    );
  };

  const contentTiles = () => {
    if(isCMSData){
      if (checkValue(activityItemList) && activityItemList.length > 0) {
        return activityItemList.map((content: any, index: number) => {
          const image = content?.image[0]?.url;
          const headline = content?.headline;
          const summary = content?.description;
          const openInNewTab = content?.openUrlInNewTab;
          const cTAText = content?.cTAText;
          let cTALink = content?.cTAUrl;
          cTALink = cTALink && appendMcicid(content?.cTAUrl, templateType, campaignUrl, referrerId);
          const price = content?.price;
  
          const contentImag = checkValue(image) ? image : config.config.staticPlaceholderImage;
  
          if (checkValue(contentImag)) {
            const contentTitle = checkValue(headline) ? headline : '';
            const contentDescription = checkValue(summary) ? summary : '';
            const clickEventTarget = openInNewTab ? '_blank' : '_self';
            const contentContentUrl = '';
            const imageAltText = '';
  
            return (
              <div
                key={`card${index}`}
                className={`
                activity-deals-wrapper
                ${activityItemList?.length < 4 ? 'no-carousal-view' : ''}
                ${activityItemList?.length < 4 && index === 1 ? 'no-carousal-view-middle' : ''}
              `}
              >
                <React.Fragment key={`activity-deals-card-${index}`}>
                  <>
                    <div>
                      <div className="image-card-wrapper">
                          {ImageCard(
                            contentImag,
                            contentTitle,
                            contentContentUrl,
                            clickEventTarget,
                            'ga4-activity-deals-card',
                            {
                              eventCategory: 'Button Click',
                              eventAction: 'Content Tile - Card',
                              eventLabel: `${contentTitle}`,
                            },
                            imageAltText,
                            ['all'],
                            null
                          )}
                      </div>
                      {tileBody(contentTitle, contentDescription)}
                    </div>
                    {checkValue(cTAText) && checkValue(cTALink) ? (
                      <div className="content-bottom-container">
                        {checkValue(price) && (
                          <Typo.HTMLText tag="p" exClass="activity-deals-body-text price">
                            {price}
                          </Typo.HTMLText>
                        )}
                        <div className="content-button-container" data-url={cTALink}>
                          <PrimaryButton
                            {...{
                              ...primaryButtonProps,
                              ...{
                                text: cTAText,
                                url: cTALink,
                                target: clickEventTarget,
                              },
                            }}
                          />
                        </div>
                      </div>
                    ) : null}
                  </>
                </React.Fragment>
              </div>
            );
          } else {
            return null;
          }
        });
      } else {
        return null;
      }
    } else {
      if (checkValue(activityDeals?.activityTiles) && typeof activityDeals?.activityTiles !== 'string') {
        return activityDeals.activityTiles?.map((content: any, index: number) => {
          const image = content?.gallery?.featuredMedia?.url;
          const headline = content?.primary?.text;
          const summary = content?.description;
          const openInNewTab = content?.clickAction?.openInNewTab;
          const cTAText = activityCtaText;
          let cTALink = content?.clickAction?.uri?.relativePath;
          cTALink = cTALink && appendMcicid(cTALink, templateType, campaignUrl, referrerId);
          const price = content?.leadTicketPrice?.priceLockup?.lockupPrice;
  
          const contentImag = checkValue(image) ? image : config.config.staticPlaceholderImage;
  
          if (checkValue(contentImag)) {
            const contentTitle = checkValue(headline) ? headline : '';
            const contentDescription = checkValue(summary) ? summary : '';
            const clickEventTarget = openInNewTab ? '_blank' : '_self';
            const contentContentUrl = '';
            const imageAltText = '';
  
            return (
              <div
                className={`
                activity-deals-wrapper
                ${activityDeals?.activityTiles?.length < 4 ? 'no-carousal-view' : ''}
                ${activityDeals?.activityTiles?.length < 4 && index === 1 ? 'no-carousal-view-middle' : ''}
              `}
              >
                <React.Fragment key={`activity-deals-card-${index}`}>
                  <>
                    <div>
                      <div className="image-card-wrapper">
                          {ImageCard(
                            contentImag,
                            contentTitle,
                            contentContentUrl,
                            clickEventTarget,
                            'ga4-activity-deals-card',
                            {
                              eventCategory: 'Button Click',
                              eventAction: 'Content Tile - Card',
                              eventLabel: `${contentTitle}`,
                            },
                            imageAltText,
                            ['all'],
                            null
                          )}
                      </div>
                      {tileBody(contentTitle, contentDescription)}
                    </div>
                    {checkValue(cTAText) && checkValue(cTALink) ? (
                      <div className="content-bottom-container">
                        {checkValue(price) && (
                          <Typo.HTMLText tag="p" exClass="activity-deals-body-text price">
                            {price}
                          </Typo.HTMLText>
                        )}
                        <div className="content-button-container" data-url={cTALink}>
                          <PrimaryButton
                            {...{
                              ...primaryButtonProps,
                              ...{
                                text: cTAText,
                                url: cTALink,
                                target: clickEventTarget,
                              },
                            }}
                          />
                        </div>
                      </div>
                    ) : null}
                  </>
                </React.Fragment>
              </div>
            );
          } else {
            return null;
          }
        });
      } else {
        return null;
      }
    }
    
  };

  const moduleCss = () => {
    const getBackgroundColor = checkValue(backgroundColor) ? backgroundColor : '';
    const darkenColor = checkValue(activityCTAColor) ? LightenDarkenColor(activityCTAColor, -0.1) : '';

    return `
      ${
        checkValue(fontWeight)
          ? `
        .activity-view .activity-deals-wrapper .cms-typo.txtBold {
          font-weight: ${fontWeight?.w7} !important;
        }
        `
          : ''
      }
      ${
        checkValue(getBackgroundColor)
          ? `
          background-color: ${getBackgroundColor} !important;
        `
          : ''
      }
      ${
        checkValue(activityCTAColor)
          ? `
        .activity-deals-carousel-container{
          .content-button-container{
            .btn {
              background-color:${activityCTAColor}!important;
              border: 1px solid ${activityCTAColor}!important;

              &:hover {
                background-color:${darkenColor}!important;
                border: 1px solid ${darkenColor}!important;
              }
            }
          }
          .common-content-panel-cta-container{
            .common-content-panel-cta-button{
              background-color:${activityCTAColor}!important;
              border: 1px solid ${activityCTAColor}!important;

              &:hover {
                background-color:${darkenColor}!important;
                border: 1px solid ${darkenColor}!important;
              }
            }
          }
        }
      `
          : ''
      }

      ${
        checkValue(activityCTATextColor)
          ? `
        .activity-deals-carousel-container{
          .content-button-container{
            .btn {
              color:${activityCTATextColor}!important;
            }
          }
          .common-content-panel-cta-container{
            .common-content-panel-cta-button{
              color:${activityCTATextColor}!important;
            }
          }
        }
      `
          : ''
      }
      ${
        checkValue(textColor)
          ? `
        .activity-deals-main-title {
          color: ${textColor} !important;
        }
        .activity-deals-main-sub-title {
          color: ${textColor} !important;
        }
        .activity-deals-body-text {
          color: ${textColor} !important;
        }
        .slick-slider .slick-arrow:before {
          border-color: ${textColor};
        }
        .slick-dots li button:before {
          color: ${textColor};
        }
        .slick-dots .slick-active button:before {
          color: ${textColor} !important;
        }
      `
          : ''
      }
    `;
  };

  const tiles = contentTiles();

  const contentTileMainContent = () => (
    <div
      className={`activity-deals-carousel-container activity-view__slider ${
        checkValue(galleryTileSize)
          ? 'activity-view__slider--' + galleryTileSize.replace(/\s+/g, '-').toLowerCase()
          : 'activity-view__slider--small'
      }`}
    >
      {checkValue(tiles) && (
        <>
          { checkValue(backgroundBarColor) && <div className="color-bar" style={{ backgroundColor: backgroundBarColor }} /> }
          <SlickSlider settings={settings} gaTracking={gaTracking}>
            {tiles}
          </SlickSlider>
        </>
      )}
    </div>
  );

  return (
    <>
      <Layout
        className="activity-view-section 112"
        css={css`
          ${moduleCss()}
        `}
        id={id}        
      >
        <div className={`activity-deals activity-view container-fluid activity-view-component-spacing`}>
          {loading ? (
            skeleton()
          ) : (
            <div>
              {checkValue(headline) && (
                <Typo.HTMLText exClass="activity-deals-main-title override-heading-font-family" tag="h2">
                  {headline}
                </Typo.HTMLText>
              )}
              {checkValue(subheadline) && (
                <Typo.HTMLText exClass="activity-deals-main-sub-title" tag="p">
                  {subheadline}
                </Typo.HTMLText>
              )}
              {contentTileMainContent()}
            </div>
          )}
        </div>
      </Layout>
    </>
  );
};

export default ActivityModule;


